import { useCallback, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { initialCadastroPesquisaListaFiltersState, useCadastroPesquisaListaFilters } from "../hooks/CadastroPesquisa_lista/filters";
import { useCadastroPesquisaListaPagination } from "../hooks/CadastroPesquisa_lista/pagination";
import { PesquisaSatisfacaoLista } from "../../domain/models/pesquisa_satisfacao_lista";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { PesquisaSatisfacaoForm } from "../schemas/CadastroPesquisa_lista/pesquisa_satisfacao";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";

/**
 * LoadPesquisasSatisfacaoFuncState
 */

type LoadPesquisasSatisfacaoFuncState = {
  field?: number | null;
  value?: string | null;
  status?: number | null;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useCadastroPesquisaListaViewModel
 */

export const useCadastroPesquisaListaViewModel = () => {

  const {
    entregaObras: {
      getAllPesquisaSatisfacaoUseCase,
      createPesquisaSatisfacaoUseCase,
      updatePesquisaSatisfacaoUseCase,
      deletePesquisaSatisfacaoUseCase,
    },
    commons: {
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
    }
  } = useDependencyInjection();

  const backOrNavigate = useBackOrNavigate();

  const { pushInfoNotification, pushErrorNotification, setPageSaving } = usePageLoader();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [pesquisasSatisfacao, setPesquisasSatisfacao] = useState<Array<PesquisaSatisfacaoLista>>();

  const [selectedPesquisaSatisfacao, setSelectedPesquisaSatisfacao] = useState<PesquisaSatisfacaoLista>();

  const [pesquisaSatisfacaoModal, setPesquisaSatisfacaoModal] = useState(false);
  const [pesquisaSatisfacaoDeleteModal, setPesquisaSatisfacaoDeleteModal] = useState(false);

  const filters = useCadastroPesquisaListaFilters();
  const pagination = useCadastroPesquisaListaPagination();

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoUseCase,
    getAllLogAlteracaoByParentUseCase,
    onSetError: message => pushErrorNotification('Erro', message),
  });

  /**
   * Funções de carregamento
   */

  const loadPesquisasSatisfacao = useCallback(async (fs?: LoadPesquisasSatisfacaoFuncState) => {
    try {
      const filteredField = evaluateParamValue(fs?.field, filters.field);
      const filteredValue = evaluateParamValue(fs?.value, filters.value);
      const filteredStatus = evaluateParamValue(fs?.status, filters.status);

      const filteredPage = fs?.page ?? pagination.page;
      const filteredRowsPerPage = fs?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllPesquisaSatisfacaoUseCase.getAllPesquisaSatisfacao(
        filteredField,
        filteredValue,
        filteredStatus,
        filteredPage,
        filteredRowsPerPage,
      );

      setPesquisasSatisfacao(getAllPesquisaSatisfacaoUseCase.items);
      pagination.setTotalItems(getAllPesquisaSatisfacaoUseCase.totalItems);
    } catch (e: unknown) {
      setPesquisasSatisfacao(undefined);
      throw new Error("Falha ao buscar dados da pagina!");
    }
  }, [
    filters,
    pagination,
    getAllPesquisaSatisfacaoUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      await loadPesquisasSatisfacao();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados da pagina!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);

      await loadPesquisasSatisfacao();

    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao buscar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadPesquisasSatisfacao,
    pushErrorNotification,
  ]);

  const handlePesquisaSatisfacaoModalOpenState = useCallback((idPesquisaSatisfacao: number | null, openState: boolean) => {
    if (openState) {
      if (idPesquisaSatisfacao && pesquisasSatisfacao) {
        setSelectedPesquisaSatisfacao(pesquisasSatisfacao.find(p => p.idPesquisaSatisfacao === idPesquisaSatisfacao));
      } else {
        setSelectedPesquisaSatisfacao(undefined);
      }
    }
    setPesquisaSatisfacaoModal(openState);
  }, [
    pesquisasSatisfacao,
  ]);

  const handlePesquisaSatisfacaoSubmit = useCallback(async (form: PesquisaSatisfacaoForm) => {
    try {
      setSaving(true);
      setPageSaving(true);

      if (selectedPesquisaSatisfacao) {
        await updatePesquisaSatisfacaoUseCase.updatePesquisaSatisfacao(
          selectedPesquisaSatisfacao.idPesquisaSatisfacao,
          form.status,
          form.codigo,
          form.descricao,
        );
      } else {
        await createPesquisaSatisfacaoUseCase.createPesquisaSatisfacao(
          form.status,
          form.codigo,
          form.descricao,
        );
      }
      await loadPesquisasSatisfacao();

      setSelectedPesquisaSatisfacao(undefined);
      setPesquisaSatisfacaoModal(false);

      pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao salvar pesquisa de satisfação!"));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    selectedPesquisaSatisfacao,
    createPesquisaSatisfacaoUseCase,
    updatePesquisaSatisfacaoUseCase,
    setPageSaving,
    loadPesquisasSatisfacao,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  const handlePesquisaSatisfacaoDeletarModalOpenState = useCallback((idPesquisaSatisfacao: number | null, openState: boolean) => {
    if (openState) {
      if (idPesquisaSatisfacao && pesquisasSatisfacao) {
        setSelectedPesquisaSatisfacao(pesquisasSatisfacao.find(p => p.idPesquisaSatisfacao === idPesquisaSatisfacao));
      } else {
        setSelectedPesquisaSatisfacao(undefined);
      }
    }
    setPesquisaSatisfacaoDeleteModal(openState);
  }, [
    pesquisasSatisfacao,
  ]);

  const handlePesquisaSatisfacaoDeleteSubmit = useCallback(async () => {
    if (selectedPesquisaSatisfacao) {
      try {
        setSaving(true);
        setPageSaving(true);

        await deletePesquisaSatisfacaoUseCase.deletePesquisaSatisfacao(
          selectedPesquisaSatisfacao.idPesquisaSatisfacao,
        );

        await loadPesquisasSatisfacao();

        setSelectedPesquisaSatisfacao(undefined);
        setPesquisaSatisfacaoDeleteModal(false);

        pushInfoNotification('Sucesso', 'Registro excluído com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, "Falha ao salvar pesquisa de satisfação!"));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    deletePesquisaSatisfacaoUseCase,
    selectedPesquisaSatisfacao,
    loadPesquisasSatisfacao,
    setPageSaving,
    pushInfoNotification,
    pushErrorNotification,
  ]);

  const handleGoToPesquisaSatisfacaoItens = useCallback((idPesquisaSatisfacao: number, openNewTab: boolean) => {
    if (openNewTab) {
      window.open(`/CadastroPesquisaItens_cadastro?id=${idPesquisaSatisfacao}`, '_blank', 'noopener,noreferrer');
    } else {
      backOrNavigate(`/CadastroPesquisaItens_cadastro?id=${idPesquisaSatisfacao}`);
    }
  }, [
    backOrNavigate,
  ]);

  const handleFieldChange = useCallback(async (field: number | null) => {
    try {
      setLoading(true);
      useCadastroPesquisaListaFilters.setState({ field });
      useCadastroPesquisaListaPagination.setState({ page: 1 });
      await loadPesquisasSatisfacao({ field, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao alterar o filtro!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadPesquisasSatisfacao,
    pushErrorNotification,
  ]);

  const handleValueChange = useCallback(async (value: string | null) => {
    try {
      setLoading(true);
      useCadastroPesquisaListaFilters.setState({ value: value });
      useCadastroPesquisaListaPagination.setState({ page: 1 });
      await loadPesquisasSatisfacao({ value, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao alterar o filtro!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadPesquisasSatisfacao,
    pushErrorNotification,
  ]);

  const handleStatusChange = useCallback(async (status: number | null) => {
    try {
      setLoading(true);
      useCadastroPesquisaListaFilters.setState({ status });
      useCadastroPesquisaListaPagination.setState({ page: 1 });
      await loadPesquisasSatisfacao({ status, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao alterar o filtro!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadPesquisasSatisfacao,
    pushErrorNotification,
  ]);

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      useCadastroPesquisaListaFilters.setState({ ...initialCadastroPesquisaListaFiltersState });
      useCadastroPesquisaListaPagination.setState({ page: 1 });
      await loadPesquisasSatisfacao({ ...initialCadastroPesquisaListaFiltersState, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao alterar o filtro!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadPesquisasSatisfacao,
    pushErrorNotification,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useCadastroPesquisaListaPagination.setState({ page: page + 1 });
      await loadPesquisasSatisfacao({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao buscar dados da página!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadPesquisasSatisfacao,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useCadastroPesquisaListaPagination.setState({ rowsPerPage, page: 1 });
      await loadPesquisasSatisfacao({ rowsPerPage, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao buscar dados da página!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadPesquisasSatisfacao,
    pushErrorNotification,
  ]);

  const handleOpenChangeHistory = useCallback(() => {
    if (selectedPesquisaSatisfacao) {
      changeHistory.handleOpenChangeHistory(selectedPesquisaSatisfacao.idPesquisaSatisfacao, LogTipoTabela.PESQUISA_SATISFACAO, true);
    }
  }, [
    selectedPesquisaSatisfacao,
    changeHistory,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    pesquisasSatisfacao: pesquisasSatisfacao ?? [],
    selectedPesquisaSatisfacao: selectedPesquisaSatisfacao ?? null,
    pesquisaSatisfacaoModal,
    pesquisaSatisfacaoDeleteModal,
    filters,
    pagination,
    changeHistory,
    handleReload,
    handlePesquisaSatisfacaoModalOpenState,
    handlePesquisaSatisfacaoSubmit,
    handlePesquisaSatisfacaoDeletarModalOpenState,
    handlePesquisaSatisfacaoDeleteSubmit,
    handleGoToPesquisaSatisfacaoItens,
    handleFieldChange,
    handleValueChange,
    handleStatusChange,
    handleClearFilters,
    handlePageChange,
    handleRowsPerPageChange,
    handleOpenChangeHistory,
  };
}